import React, { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { VelavuDevice, VelavuFloor } from "velavu-js-api";
import IconWarning from "../../../../../dynamicicons/icon-warning";
import Divider from "../../../../../elements/divider";
import DynamicBattery from "../../../../../elements/dynamic-battery";
import HoverTooltip from "../../../../../elements/hover-tooltip";
import SelectableCard from "../../../../../elements/selectable-card";
import StatusIndicator from "../../../../../elements/status-indicator";
import { getLocationFloorID } from "../../../../../helper/floor-helper";
import { getDeviceStatus } from "../../../../../helper/status-helper";
import DevicesFilter from "../devices-filter/devices-filter";
import * as styles from "./site-detail-anchors.module.scss";
import DevicesToolbar from "../devices-toolbar/devices-toolbar";
import FloorFilter from "../../../../../data/floor-filter";
import useMenuAnchorState from "../../../../../state/menu-anchor-state";
import { PinnedCascadingPanelStackPortal } from "../../../../../elements/cascading-panel-stack";
import AnchorSelectPanel from "./anchor-select-panel";
import { useHasPermission } from "../../../../sign-in-context/sign-in-context";

interface SiteDetailAnchorProps {
	siteID: string;
	anchors: VelavuDevice[] | undefined;
	onSelectAnchor?: (deviceID: string) => void;
	onHoverAnchor?: (deviceID: string | undefined) => void;
	filterTargetFloor?: VelavuFloor;
	onChangeFloorFilter: (floor: FloorFilter) => void;
	onPinNewAnchor?: (anchor: VelavuDevice) => void;
	showFloorFilter?: boolean;
}

const Anchor = memo((props: { anchor: VelavuDevice }) => {
	const { t } = useTranslation();

	return (
		<>
			<div className={styles.row}>
				<StatusIndicator
					status={getDeviceStatus(props.anchor)}
					className={styles.indicator}
				/>
				<span className={styles.anchorName}>
					{props.anchor.asset?.name ?? props.anchor.id}
				</span>
				{!getLocationFloorID(props.anchor.location) && <Warning />}
			</div>
			<div className={styles.battery}>
				<span
					style={{ marginRight: 4 }}
					className={styles.batteryLevel}
				>
					{props.anchor.state?.power?.battery_level !== undefined
						? `${props.anchor.state.power.battery_level}%`
						: t("common.na")}
				</span>
				<DynamicBattery
					level={
						props.anchor.state?.power?.battery_level !== undefined
							? props.anchor.state?.power.battery_level / 100
							: -1
					}
					charging={
						props.anchor.state !== undefined &&
						"usb_power" in props.anchor.state.power &&
						props.anchor.state.power.usb_power
					}
				/>
			</div>
		</>
	);
});

function Warning() {
	const { t } = useTranslation();

	return (
		<HoverTooltip
			wrapperClassName={styles.warning}
			className={styles.tooltip}
			tooltip={
				<>
					<span>{t("dashboard.site_anchors.warning_title")}</span>
					<div className={styles.divider} />
					<span className={styles.bottom}>
						{t("dashboard.site_anchors.warning_body")}
					</span>
				</>
			}
		>
			<IconWarning />
		</HoverTooltip>
	);
}

export default function SiteDetailAnchors(props: SiteDetailAnchorProps) {
	const { t } = useTranslation();

	const canWriteDevices = useHasPermission("devices:write");

	const [searchText, setSearchText] = useState("");
	const [showFilterMenu, setShowFilterMenu] = useState(false);
	const [filterBattery, setFilterBattery] = useState<boolean | undefined>(
		undefined,
	);
	const [filterStatus, setFilterStatus] = useState<boolean | undefined>(
		undefined,
	);

	const toggleFilterMenu = useCallback(
		() => setShowFilterMenu((it) => !it),
		[setShowFilterMenu],
	);

	const filteredAnchors = useMemo(() => {
		let anchors = props.anchors ?? [];

		if (filterStatus !== undefined) {
			anchors = anchors.filter(
				(anchor) => anchor.online === filterStatus,
			);
		}

		if (filterBattery !== undefined) {
			anchors = anchors.filter(
				(anchor) =>
					anchor.state?.power?.battery_level &&
					anchor.state?.power?.battery_level < 15,
			);
		}

		anchors = anchors.filter((anchor) =>
			anchor.id.toLowerCase().includes(searchText.toLowerCase()),
		);

		const filterTargetFloor = props.filterTargetFloor;
		if (filterTargetFloor !== undefined) {
			anchors = anchors.filter(
				(a) => getLocationFloorID(a?.location) === filterTargetFloor.id,
			);
		}

		return anchors;
	}, [
		props.filterTargetFloor,
		props.anchors,
		searchText,
		filterStatus,
		filterBattery,
	]);

	const propsOnSelectAnchor = props.onSelectAnchor;
	const propsOnHoverAnchor = props.onHoverAnchor;
	const listContent = useMemo(
		() =>
			filteredAnchors.length > 0 ? (
				filteredAnchors.map((anchor) => (
					<SelectableCard
						key={anchor.id}
						className={styles.anchorCard}
						onClick={() => {
							propsOnSelectAnchor?.(anchor.id);
							propsOnHoverAnchor?.(undefined);
						}}
						onMouseEnter={() => propsOnHoverAnchor?.(anchor.id)}
						onMouseLeave={() => propsOnHoverAnchor?.(undefined)}
					>
						<Anchor anchor={anchor} />
					</SelectableCard>
				))
			) : (
				<div className={styles.emptyLabel}>
					{t("common.no_results")}
				</div>
			),
		[filteredAnchors, propsOnSelectAnchor, propsOnHoverAnchor, t],
	);

	const [menuPinAnchor, openPinMenu, closePinMenu] = useMenuAnchorState();

	return (
		<>
			{menuPinAnchor && (
				<PinnedCascadingPanelStackPortal refPosition={menuPinAnchor}>
					<AnchorSelectPanel
						onClose={closePinMenu}
						siteID={props.siteID}
						onSelect={props.onPinNewAnchor}
					/>
				</PinnedCascadingPanelStackPortal>
			)}

			<div className={styles.anchors}>
				<DevicesToolbar
					searchText={searchText}
					setSearchText={setSearchText}
					levelSelection={props.filterTargetFloor?.level}
					onChangeFloor={props.onChangeFloorFilter}
					showFloorFilter={props.showFloorFilter}
					highlightFilter={
						filterBattery !== undefined ||
						filterStatus !== undefined
					}
					showFilterMenu={showFilterMenu}
					onClickShowFilterMenu={toggleFilterMenu}
					onClickAdd={canWriteDevices ? openPinMenu : undefined}
					addFocused={menuPinAnchor !== null}
				/>

				<Divider />
				{showFilterMenu && (
					<>
						<DevicesFilter
							filterStatus={filterStatus}
							selectStatus={setFilterStatus}
							filterBattery={filterBattery}
							selectBattery={setFilterBattery}
						/>
						<Divider />
					</>
				)}
				<div className={styles.content}>{listContent}</div>
			</div>
		</>
	);
}
